import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ShieldCheck, GraduationCap, Award, Phone, CheckSquare, FileSignature, Wallet, Target, Users,
  ArrowRight, AlertTriangle, CalendarClock, UserPlus, FilePlus2, Upload, CalendarPlus, ListChecks, Sparkles,
} from "lucide-react";
import { toast } from "sonner";

import { AppShell } from "@/components/layout/AppShell";
import { LoadingState } from "@/components/common/StateBlocks";
import { TaskItem } from "@/components/run/TaskItem";
import { dashboardService } from "@/services/dashboardService";
import { onboardingService } from "@/services/onboardingService";
import { useAuth } from "@/context/AuthContext";
import { useCrmActions } from "@/context/CrmActionsContext";
import { fmtCents } from "@/lib/format";
import { fmtTime, fmtWeekday } from "@/lib/datetime";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";

const CAT_ICON = { license: ShieldCheck, education: GraduationCap, membership: Award, followup: Phone, task: CheckSquare, transaction: FileSignature, commission: Wallet, goal: Target, lead: Users };
const PRIORITY = {
  critical: "border-l-[#5B1E22] bg-[#5B1E22]/5 text-[#5B1E22]",
  high: "border-l-amber-500 bg-amber-50 text-amber-800",
  medium: "border-l-[#102A43] bg-[#102A43]/5 text-[#102A43]",
  low: "border-l-border bg-secondary/40 text-muted-foreground",
  info: "border-l-border bg-secondary/40 text-muted-foreground",
};
const partOfDay = () => { const h = new Date().getHours(); return h < 12 ? "Good morning" : h < 18 ? "Good afternoon" : "Good evening"; };

const SectionTitle = ({ children, action }) => (
  <div className="mb-3 flex items-center justify-between">
    <h2 className="font-heading text-lg font-semibold text-foreground">{children}</h2>
    {action}
  </div>
);

export default function Dashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { openAddContact, openNewLead, openNewTransaction, openNewTask, openNewEvent, openUploadDocument } = useCrmActions();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [onboarding, setOnboarding] = useState(null);

  useEffect(() => {
    let active = true;
    onboardingService.get().then((o) => {
      if (!active) return;
      if (o.status === "not_started") { navigate("/welcome", { replace: true }); return; }
      setOnboarding(o);
    }).catch(() => {});
    return () => { active = false; };
  }, [navigate]);

  const load = useCallback(async () => {
    try { setData(await dashboardService.get()); } catch { toast.error("Could not load your dashboard"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const h = () => load();
    window.addEventListener("crm:changed", h);
    return () => window.removeEventListener("crm:changed", h);
  }, [load]);

  if (loading) return <AppShell><div className="py-20"><LoadingState label="Reviewing your business" /></div></AppShell>;
  if (!data) return <AppShell><div className="py-20 text-center text-muted-foreground">Dashboard unavailable.</div></AppShell>;

  const name = data.greeting?.name || user?.first_name || "there";
  const { brief = [], attention = [], today = {}, upcoming = [], snapshot = {}, goals = [], pipeline = [], onboarding_nudge } = data;
  const hasData = brief.length || attention.length || (today.tasks?.length) || (today.followups?.length) || (today.events?.length) || snapshot.active_leads || snapshot.active_transactions || goals.length;

  const QUICK = [
    { label: "Add Contact", icon: UserPlus, fn: () => openAddContact() },
    { label: "Add Lead", icon: Users, fn: () => openNewLead() },
    { label: "Add Transaction", icon: FilePlus2, fn: () => openNewTransaction() },
    { label: "Add Task", icon: ListChecks, fn: () => openNewTask() },
    { label: "Add Event", icon: CalendarPlus, fn: () => openNewEvent() },
    { label: "Upload Document", icon: Upload, fn: () => openUploadDocument() },
  ];

  const Kpi = ({ label, value, route, testid, sub }) => (
    <button onClick={() => route && navigate(route)} data-testid={testid}
      className="rounded-xl border border-border bg-card p-4 text-left shadow-card transition-colors hover:border-brand-gold/50">
      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className="mt-1.5 font-heading text-2xl font-bold text-foreground">{value}</p>
      {sub && <p className="mt-0.5 text-[11px] text-muted-foreground">{sub}</p>}
    </button>
  );

  return (
    <AppShell>
      <div className="space-y-8" data-testid="dashboard-page">
        {/* Greeting */}
        <div>
          <h1 className="font-heading text-3xl font-bold text-foreground sm:text-4xl" data-testid="dashboard-greeting">{partOfDay()}, {name}.</h1>
          <p className="mt-1 text-muted-foreground">Here's what needs your attention today.</p>
        </div>

        {/* Setup progress card (only while onboarding is incomplete) */}
        {onboarding?.show_setup_card && (
          <div className="flex flex-col items-start justify-between gap-3 rounded-xl border border-brand-navy/15 bg-brand-navy/5 p-4 sm:flex-row sm:items-center" data-testid="dashboard-setup-card">
            <div className="flex w-full items-start gap-3 sm:w-auto">
              <ListChecks className="mt-0.5 h-5 w-5 text-brand-navy" />
              <div className="min-w-0 flex-1">
                <p className="font-medium text-foreground">Finish your AgentHQ setup</p>
                <p className="text-sm text-muted-foreground">{onboarding.completed_count} of {onboarding.total_steps} complete</p>
                <Progress value={onboarding.percent} className="mt-2 h-1.5 w-full sm:w-56" />
              </div>
            </div>
            <Button onClick={() => navigate("/welcome")} data-testid="dashboard-continue-setup" className="bg-brand-navy hover:bg-brand-deep-blue">Continue Setup <ArrowRight className="h-4 w-4" /></Button>
          </div>
        )}

        {/* Onboarding nudge */}
        {onboarding_nudge?.show && (
          <div className="flex flex-col items-start justify-between gap-3 rounded-xl border border-brand-gold/40 bg-brand-gold/10 p-4 sm:flex-row sm:items-center" data-testid="onboarding-nudge">
            <div className="flex items-start gap-3">
              <Sparkles className="mt-0.5 h-5 w-5 text-brand-gold" />
              <div><p className="font-medium text-foreground">{onboarding_nudge.title}</p><p className="text-sm text-muted-foreground">{onboarding_nudge.description}</p></div>
            </div>
            <Button onClick={() => navigate(onboarding_nudge.related_route)} data-testid="onboarding-nudge-cta">Finish setup <ArrowRight className="h-4 w-4" /></Button>
          </div>
        )}

        {!hasData && !onboarding_nudge?.show && (
          <Card><CardContent className="p-8 text-center" data-testid="dashboard-empty">
            <Sparkles className="mx-auto mb-3 h-8 w-8 text-brand-gold" />
            <h2 className="font-heading text-xl font-semibold text-foreground">Your business command center starts here.</h2>
            <p className="mx-auto mt-2 max-w-xl text-sm text-muted-foreground">As you add contacts, leads, transactions, tasks, goals, and professional requirements, AgentHQ will organize what needs your attention.</p>
          </CardContent></Card>
        )}

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3 lg:gap-8">
        {/* Daily Business Brief */}
        {brief.length > 0 && (
          <section data-testid="business-brief" className="order-3 lg:order-none lg:col-span-3">
            <SectionTitle>Your Business Brief</SectionTitle>
            <div className="grid gap-2 sm:grid-cols-2">
              {brief.map((b, i) => {
                const Icon = CAT_ICON[b.category] || CheckSquare;
                return (
                  <button key={i} onClick={() => b.related_route && navigate(b.related_route)} data-testid={`brief-item-${i}`}
                    className={`flex items-center gap-3 rounded-lg border border-l-4 p-3 text-left ${PRIORITY[b.priority]}`}>
                    <Icon className="h-4 w-4 shrink-0" />
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium text-foreground">{b.value != null && b.category === "commission" ? `${fmtCents(b.value)} ${b.title.toLowerCase()}` : b.title}</p>
                      <p className="truncate text-xs text-muted-foreground">{b.description}</p>
                    </div>
                  </button>
                );
              })}
            </div>
          </section>
        )}

            {/* Needs Your Attention */}
            {attention.length > 0 && (
              <section data-testid="attention-section" className="order-1 lg:order-none lg:col-span-2">
                <SectionTitle>Needs Your Attention</SectionTitle>
                <div className="space-y-2">
                  {attention.map((a, i) => (
                    <div key={i} className="flex items-center justify-between gap-3 rounded-lg border border-border bg-card px-4 py-3 shadow-card" data-testid={`attention-item-${i}`}>
                      <div className="flex items-center gap-3">
                        <AlertTriangle className={`h-4 w-4 ${a.priority === "critical" ? "text-[#5B1E22]" : a.priority === "high" ? "text-amber-600" : "text-[#102A43]"}`} />
                        <div><p className="text-sm font-medium text-foreground">{a.title}</p><p className="text-xs text-muted-foreground">{a.description}</p></div>
                      </div>
                      <Button variant="outline" size="sm" onClick={() => navigate(a.related_route)} data-testid={`attention-cta-${i}`}>{a.action_label || "View"}</Button>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* Today's Work */}
            <section data-testid="today-section" className="order-2 lg:order-none lg:col-span-2">
              <SectionTitle action={<Button variant="ghost" size="sm" onClick={() => navigate("/calendar")}>Calendar <ArrowRight className="h-3.5 w-3.5" /></Button>}>Today's Work</SectionTitle>
              {(today.tasks?.length || today.followups?.length || today.events?.length) ? (
                <div className="space-y-2">
                  {today.tasks?.map((t) => <TaskItem key={t.id} task={t} onChanged={load} onEdit={() => navigate("/tasks")} />)}
                  {today.followups?.map((f) => (
                    <button key={f.id} onClick={() => navigate(f.contact?.id ? `/crm/${f.contact.id}` : "/follow-ups")} data-testid={`today-followup-${f.id}`}
                      className="flex w-full items-center gap-3 rounded-lg border border-border bg-card px-4 py-3 text-left hover:bg-secondary">
                      <Phone className="h-4 w-4 text-accent" />
                      <div className="min-w-0 flex-1"><p className="truncate text-sm font-medium">{f.contact ? `${f.contact.first_name} ${f.contact.last_name}` : "Follow-up"}</p><p className="text-xs text-muted-foreground">Follow-up due today</p></div>
                    </button>
                  ))}
                  {today.events?.map((e) => (
                    <button key={e.id} onClick={() => navigate("/calendar")} data-testid={`today-event-${e.id}`}
                      className="flex w-full items-center gap-3 rounded-lg border border-border bg-card px-4 py-3 text-left hover:bg-secondary">
                      <CalendarClock className="h-4 w-4 text-accent" />
                      <div className="min-w-0 flex-1"><p className="truncate text-sm font-medium">{e.title}</p><p className="text-xs text-muted-foreground">{e.all_day ? "All day" : fmtTime(e.start_at)}</p></div>
                    </button>
                  ))}
                </div>
              ) : <p className="rounded-lg border border-dashed border-border p-6 text-center text-sm text-muted-foreground" data-testid="today-empty">Nothing scheduled for today. Enjoy the open space or get ahead.</p>}
            </section>

            {/* Upcoming */}
            {upcoming.length > 0 && (
              <section data-testid="upcoming-section" className="order-5 lg:order-none lg:col-span-2 lg:col-start-1">
                <SectionTitle action={<Button variant="ghost" size="sm" onClick={() => navigate("/calendar")}>View all <ArrowRight className="h-3.5 w-3.5" /></Button>}>Upcoming · Next 7 days</SectionTitle>
                <div className="space-y-2">
                  {upcoming.map((e) => (
                    <button key={e.id} onClick={() => navigate(e.related?.type === "transaction" ? `/transactions/${e.related.id}` : "/calendar")} data-testid={`upcoming-${e.id}`}
                      className="flex w-full items-center gap-3 rounded-lg border border-border bg-card px-4 py-2.5 text-left hover:bg-secondary">
                      <div className="w-28 shrink-0 text-xs font-medium text-muted-foreground">{fmtWeekday(e.start_at)}</div>
                      <p className="min-w-0 flex-1 truncate text-sm">{e.title}</p>
                      <span className="text-xs text-muted-foreground">{e.all_day ? "All day" : fmtTime(e.start_at)}</span>
                    </button>
                  ))}
                </div>
              </section>
            )}

            {/* Business Snapshot */}
            <section data-testid="snapshot-section" className="order-4 lg:order-none lg:col-start-3 lg:row-start-2">
              <SectionTitle>Business Snapshot</SectionTitle>
              <div className="grid grid-cols-2 gap-2">
                <Kpi label="Active Leads" value={snapshot.active_leads ?? 0} route="/pipeline" testid="kpi-active-leads" />
                <Kpi label="Active Deals" value={snapshot.active_transactions ?? 0} route="/transactions" testid="kpi-active-tx" />
                <Kpi label="Pending Commission" value={fmtCents(snapshot.pending_commission)} route="/commissions" testid="kpi-pending-commission" sub="Not yet collected" />
                <Kpi label="Paid YTD" value={fmtCents(snapshot.paid_commission_ytd)} route="/commissions" testid="kpi-paid-commission" sub={`${snapshot.closed_transactions_ytd ?? 0} closed YTD`} />
              </div>
            </section>

            {/* Goals */}
            {goals.length > 0 && (
              <section data-testid="goals-section" className="order-6 lg:order-none lg:col-start-3">
                <SectionTitle action={<Button variant="ghost" size="sm" onClick={() => navigate("/goals")}>View Goals <ArrowRight className="h-3.5 w-3.5" /></Button>}>Goal Progress</SectionTitle>
                <div className="space-y-3">
                  {goals.map((g) => (
                    <Card key={g.id} data-testid={`dashboard-goal-${g.id}`}><CardContent className="p-4">
                      <div className="flex items-center justify-between text-sm"><span className="font-medium capitalize text-foreground">{g.period_type} {g.goal_type.replace("_", " ")}</span><span className="font-semibold">{g.progress_percent}%</span></div>
                      <Progress value={g.progress_percent} className="my-2 h-2" />
                      <p className="text-xs text-muted-foreground">Tracking automatically from your business data.</p>
                    </CardContent></Card>
                  ))}
                </div>
              </section>
            )}

            {/* Pipeline snapshot */}
            {pipeline.length > 0 && (
              <section data-testid="pipeline-section" className="order-7 lg:order-none lg:col-start-3">
                <SectionTitle action={<Button variant="ghost" size="sm" onClick={() => navigate("/pipeline")}>Open <ArrowRight className="h-3.5 w-3.5" /></Button>}>Pipeline</SectionTitle>
                <Card><CardContent className="space-y-1.5 p-4">
                  {pipeline.map((s) => (
                    <div key={s.code} className="flex items-center justify-between text-sm">
                      <span className="truncate text-muted-foreground">{s.stage}</span>
                      <Badge variant="secondary">{s.count}</Badge>
                    </div>
                  ))}
                </CardContent></Card>
              </section>
            )}

            {/* Quick Actions */}
            <section data-testid="quick-actions-section" className="order-8 lg:order-none lg:col-start-3">
              <SectionTitle>Quick Actions</SectionTitle>
              <div className="grid grid-cols-2 gap-2">
                {QUICK.map((q) => (
                  <button key={q.label} onClick={q.fn} data-testid={`quick-${q.label.toLowerCase().replace(/ /g, "-")}`}
                    className="flex items-center gap-2 rounded-lg border border-border bg-card px-3 py-2.5 text-left text-sm font-medium transition-colors hover:border-brand-gold/50 hover:bg-secondary">
                    <q.icon className="h-4 w-4 text-accent" /> {q.label}
                  </button>
                ))}
              </div>
            </section>
        </div>
      </div>
    </AppShell>
  );
}
